import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import capitalize from 'lodash/capitalize';
import { Banner } from '@figma/fpl-components';
import fplTokens from '@figma/fpl-tokens/css-vars';
import { Typography } from '@/UI/AppTypography/AppTypography';
import { I18N_KEYS } from '@/language/keys';
import { Flex } from '@/UI/styles';
import { formatLongDate } from '@/utils/date.utils';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import type { CreditLimitStatus } from '@/api/credits/credits.api.types';

/**
 * "Out of credits" banner blocking a run, in two credit regimes:
 * - EAP (weave credits): pass `status` — a credit-limit window (daily / weekly /
 *   monthly); copy names the window + when it resets.
 * - Figma-credit consumption (`weave-file-figma-credits-consumption` on): omit
 *   `status` — the user is out of real Figma AI credits, which have no reset
 *   window, so the copy is windowless.
 *
 * An optional `cta` (the request/manage-credits button) renders inside the
 * banner: inline to the right of the copy in the `node` variant, or stacked
 * below it in the narrower `panel` variant.
 *
 * Rendered as a notice box in two surfaces via `variant`:
 * - 'node'  (per-model-node footer, "Run model"): window is sentence-initial,
 *   so it's capitalized ("Daily credits reset …").
 * - 'panel' (right-panel "Run selected nodes"): window is mid-sentence and
 *   stays lowercase ("Not enough daily credits …").
 */
const FigmaEmbedOutOfCreditsMsg = ({
  status,
  variant = 'panel',
  cta,
}: {
  status?: CreditLimitStatus;
  variant?: 'node' | 'panel';
  cta?: ReactNode;
}) => {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();
  let text: string;

  // TODO(yhuang): finalize copy
  if (status?.type === 'weave_credits') {
    const date = formatLongDate(status.resetAt);
    text =
      variant === 'node'
        ? t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.OUT_OF_CREDITS_BETA, {
            window: capitalize(status.window),
            date,
          })
        : t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.RUN_MODELS_SECTION.OUT_OF_CREDITS_BETA, {
            window: status.window,
            date,
          });
  } else if (status?.type === 'figma_credits' && status?.resetAt) {
    const date = formatLongDate(status.resetAt);
    text =
      variant === 'node'
        ? t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE.LOW_CREDITS_MESSAGE, { date })
        : t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.RUN_MODELS_SECTION.NOT_ENOUGH_CREDITS);
  } else {
    text =
      variant === 'node'
        ? t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE.LOW_CREDITS_MESSAGE_SHORT)
        : t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.RUN_MODELS_SECTION.NOT_ENOUGH_CREDITS);
  }

  const isNodeVariant = variant === 'node';

  if (isFplMigrationEnabled && !isNodeVariant) {
    return (
      <Banner.Inset variant="default">
        <Banner.Message>{text}</Banner.Message>
        {cta}
      </Banner.Inset>
    );
  }

  return (
    <Flex
      sx={{
        gap: '8px',
        width: '100%',
        display: 'flex',
        backgroundColor: isFplMigrationEnabled ? fplTokens.color.bgSecondary : 'var(--color-bg-selected)',
        padding: '8px',
        borderRadius: '5px',
        flexDirection: isNodeVariant ? 'row' : 'column',
        alignItems: isFplMigrationEnabled ? 'center' : 'flex-start',
        alignSelf: 'stretch',
      }}
    >
      <Typography variant="body-sm-rg" color="var(--color-text)" sx={{ flexGrow: 1 }}>
        {text}
      </Typography>
      {cta}
    </Flex>
  );
};

export default FigmaEmbedOutOfCreditsMsg;
